/**
 * Who holds the STT vendor connection — the single fact both the browser and
 * stt-api must agree on.
 *
 * We do not transcribe audio on our own infrastructure and never have. There is
 * no STT model in this system; every provider is a thin client to a vendor API
 * (`wss://api.openai.com/v1/realtime`, `wss://api.x.ai/v1/stt`, Groq REST).
 * The only thing that varies is which side opens that vendor connection:
 *
 *   direct   the browser talks to the vendor itself, using a short-lived
 *            session token the vendor is willing to accept from a client.
 *
 *   relayed  stt-api opens the vendor connection and the browser streams audio
 *            through us to reach it.
 *
 * `relayed` exists for exactly one reason — credential transport. xAI
 * authenticates with an `Authorization` header, and the browser WebSocket API
 * cannot set headers, so the audio has to arrive somewhere that can. Audio
 * crossing our servers is a side effect of that constraint. It never means we
 * transcribe, and nothing here should be read as a processing decision.
 *
 * WHY THIS LIVES IN ONE PLACE: the browser and stt-api used to answer this
 * question separately — the client by capability detection, the server by a
 * stored `server` config flag that no schema declared and every admin save
 * stripped. When they disagreed the client relayed audio and the server
 * discarded it as "client-only", silently, with no error on either side. Every
 * room created after the rc30 deploy lost voice that way (#3447). Deriving the
 * answer here, from the provider, is what makes disagreement impossible.
 *
 * Adding a provider means adding one entry to VENDOR_LINK_BY_PROVIDER. Do not
 * re-derive this anywhere else.
 */
export type VendorLink = 'direct' | 'relayed';

/**
 * The link type each STT provider requires. Keep this exhaustive: an unknown
 * provider resolves to 'direct', which fails loudly in the browser rather than
 * silently discarding audio server-side.
 */
const VENDOR_LINK_BY_PROVIDER: Record<string, VendorLink> = {
    // Browser-held: these vendors accept an ephemeral client-side session token.
    'openai': 'direct',
    'openai-realtime': 'direct',
    'groq': 'direct',
    // Server-held: xAI requires an Authorization header, which a browser cannot
    // set on a WebSocket. See the note above — this is credential transport.
    'xai': 'relayed',
};

/**
 * Resolve the link type for a provider name.
 *
 * Unknown providers resolve to 'direct' deliberately. A wrong 'direct' surfaces
 * immediately as a client-side provider construction error; a wrong 'relayed'
 * would reproduce #3447, where audio is accepted and dropped in silence.
 */
export function vendorLinkFor(provider: string | null | undefined): VendorLink {
    if (!provider) return 'direct';
    return VENDOR_LINK_BY_PROVIDER[provider] ?? 'direct';
}

/**
 * True when stt-api must hold the vendor connection for this config.
 *
 * `server: true` on a stored config is still honoured so that any row
 * deliberately pinned to server-held stays that way. It can only ever turn
 * relaying ON — it can no longer turn it off, which is the direction that broke
 * rooms when the flag went missing.
 */
export function requiresVendorRelay(
    sttConfig: { provider?: string | null; server?: boolean } | null | undefined,
): boolean {
    if (!sttConfig) return false;
    if (sttConfig.server === true) return true;
    return vendorLinkFor(sttConfig.provider) === 'relayed';
}
